import React, { forwardRef, useImperativeHandle, useState } from 'react';
import { Alert, Button, Modal, Space, Typography } from 'antd';
import { LoadingState, useLoading } from '@monorepo/react-components';
import { useNavigate } from 'react-router-dom';
import { useNotification } from '../../../hooks/use-notification';
import { useStore } from '../../../helpers/use-store';
import { WarningOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';

export interface DeleteSeriesModalActions {
  open: (seriesId: string) => void;
}

const DeleteSeriesModal = forwardRef<DeleteSeriesModalActions>((_, ref) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [seriesId, setSeriesId] = useState<string | null>(null);
  const { loadingState, updateLoadingState } = useLoading();
  const {
    dataStore: { seriesStore },
  } = useStore();
  const notification = useNotification();
  const navigate = useNavigate();

  useImperativeHandle(ref, () => ({
    open: (id: string) => {
      setSeriesId(id);
      setIsOpen(true);
    },
  }));

  const handleDelete = async () => {
    if (!seriesId) return;

    try {
      updateLoadingState(LoadingState.Loading);
      await seriesStore.delete(seriesId);

      notification.success({
        message: t('common.success'),
        description: t('series.deleteModal.success'),
      });

      navigate('/series');
    } catch (error) {
      console.error('Failed to delete series:', error);
      notification.error({
        message: t('common.error'),
        description: t('series.deleteModal.error'),
      });
    } finally {
      updateLoadingState(LoadingState.Loaded);
      handleCancel();
    }
  };

  const handleCancel = () => {
    setIsOpen(false);
    setSeriesId(null);
  };

  return (
    <Modal
      title={t('series.deleteModal.title')}
      open={isOpen}
      onCancel={handleCancel}
      footer={[
        <Button key="cancel" onClick={handleCancel}>
          {t('series.deleteModal.cancel')}
        </Button>,
        <Button
          key="delete"
          danger
          type="primary"
          loading={loadingState === LoadingState.Loading}
          onClick={handleDelete}
        >
          {t('common.delete')}
        </Button>,
      ]}
    >
      <Space direction="vertical" size={16} style={{ width: '100%' }}>
        <Typography.Text strong style={{ fontSize: '16px' }}>
          {t('series.deleteModal.areYouSure')}
        </Typography.Text>
        <div
          style={{
            background: '#fffbe6',
            border: '1px solid #ffe58f',
            borderRadius: '6px',
            padding: '12px',
          }}
        >
          <Space align="start">
            <WarningOutlined style={{ color: '#faad14', fontSize: '16px' }} />
            <div>
              <Typography.Text
                strong
                style={{
                  color: '#d48806',
                  display: 'block',
                  marginBottom: '4px',
                }}
              >
                {t('series.deleteModal.thisWill')}
              </Typography.Text>
              <ul style={{ margin: '0', paddingLeft: '16px', color: '#666' }}>
                <li>{t('series.deleteModal.cancelScheduled')}</li>
                <li>{t('series.deleteModal.removeVideos')}</li>
                <li>{t('series.deleteModal.deleteSettings')}</li>
                <li>{t('series.deleteModal.keepVideos')}</li>
              </ul>
            </div>
          </Space>
        </div>
      </Space>
    </Modal>
  );
});

export default DeleteSeriesModal;
